import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { Accordion, Row, Col, Modal } from "react-bootstrap";
import moment from "moment";
import ShowMoreText from 'react-show-more-text';

import CustomButton from "../../CustomButton";
import {
    accordionArrowIcon,
    deleteIcon,
    editIcon,
    iconReport,
    iconSearch,
    plusIcon,
} from "../../helpers/iconHelpers";
import SearchField from "../../dump/menuComponents/SearchField";
import { deleteZone } from "../../../action/CompaniesActions";

export default function ZonesList(props) {
    const navigate = useNavigate();
    const zonesListRef = useRef(null);
    const zoneRefs = useRef({});
    const { zones = [], filteredZones = [], setMode, setEditedZone, searchText, setSearchText, getZones, zoomToZone, highlightedZoneId, highlightZoneOnMap } = props;
    const { t } = useTranslation('translation', { keyPrefix: 'zonesPage' });
    const dispatch = useDispatch();
    const company = useSelector(state => state.company.company);
    const companyId = company.company_id;
    const [confirmModal, setConfirmModal] = useState(false);
    const [deletedZone, setDeletedZone] = useState({
        id: null,
        name: null
    });

    useEffect(() => {
        if (!highlightedZoneId) return;

        const container = zonesListRef.current;
        if (!container) return;

        Object.values(zoneRefs.current).forEach(el => {
            if (el) el.classList.remove('highlighted-zone');
        });

        const el = zoneRefs.current[highlightedZoneId];
        if (!el) return;

        el.classList.add('highlighted-zone');

        requestAnimationFrame(() => {
            setTimeout(() => {
                const containerRect = container.getBoundingClientRect();
                const elementRect = el.getBoundingClientRect();
                const offset = elementRect.top - containerRect.top - 10;

                container.scrollTo({
                    top: container.scrollTop + offset,
                    behavior: 'smooth',
                });
            }, 350);
        });

        const timeoutId = setTimeout(() => el.classList.remove('highlighted-zone'), 3000);

        return () => clearTimeout(timeoutId);
    }, [highlightedZoneId]);

    const handleAccordionOpen = (eventKey) => {
        const element = zoneRefs.current[eventKey];
        const container = zonesListRef.current;
        if (!element || !container) return;

        requestAnimationFrame(() => {
            setTimeout(() => {
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();

                const offset = elementRect.top - containerRect.top - 5;

                container.scrollTo({
                    top: container.scrollTop + offset,
                    behavior: 'smooth',
                });
            }, 350)
        });
        highlightZoneOnMap(eventKey);
    };

    const setCreationMode = () => {
        setEditedZone(null);
        setMode('CREATE');
    }

    const confirmZoneDelete = () => {
        closeConfirmDialog();
        deleteZone(companyId, deletedZone.id).then(res => {
            const result = res.data.result;
            if (result === 'OK') {
                getZones();
                toastr.success("", t('successDeletingZone'));
            } else {
                toastr.error(t('errorDeletingZone'));
            }
        }).catch(err => {
            console.log(err, "delete Zone error");
            toastr.error(t('errorDeletingZone'));
        });
    }

    const onSearch = (e) => {
        const value = e.target.value;
        setSearchText(value);
    }

    const closeConfirmDialog = () => {
        setDeletedZone({
            id: null,
            name: null
        });
        setConfirmModal(false);
    }

    const openConfirmDialog = (id, name) => {
        setDeletedZone({
            id,
            name
        });
        setConfirmModal(true);
    }

    const generateReport = (id) => {
        dispatch({
            type: 'SET_ZONES_REPORT',
            payload: {
                reportId: '20',
                zoneId: id
            }
        });
        navigate('/reports');
        history.replaceState({ prevPath: '/zones' }, null, null)
    }

    const truncateDecimals = (value, decimals) => {
        const factor = Math.pow(10, decimals);
        return Math.trunc(value * factor) / factor;
    }

    return (
        <>
            <div className={`zones-list-component ${!zones.length ? 'no-zones' : ''}`}>
                <div className="list-header">
                    <h2>{t('zones')}</h2>
                    <CustomButton
                        variant='primary'
                        prefix="flex-btn add-zone-btn"
                        onClick={setCreationMode}
                    >
                        {plusIcon}
                        {t('addZone')}
                    </CustomButton>
                </div>
                <SearchField term={searchText} onSearch={onSearch} placeholder={`${t('searchZones')}...`} />
                <div className="zones-list" ref={zonesListRef}>
                    <Accordion flush onSelect={(eventKey) => handleAccordionOpen(eventKey)}>
                        {filteredZones.map(zone => (
                            <Accordion.Item
                                key={`zone_${zone.id}`}
                                eventKey={zone.id}
                                ref={(el) => (zoneRefs.current[zone.id] = el)}
                            >
                                <Accordion.Header>
                                    <Row>
                                        <Col xs={9} className="left-column">
                                            {accordionArrowIcon}
                                            <div className="zone-base-info">
                                                <div className="zone-name">{zone.zone_name}</div>
                                                <div className="zone-visits">{t('visitsToday', { visits: zone.visits || 0 })}</div>
                                            </div>
                                        </Col>
                                        <Col xs={3} className="right-column">
                                            <span className="icon-btn" onClick={(e) => {
                                                e.stopPropagation();
                                                setEditedZone(zone);
                                                setMode('EDIT');
                                            }}>
                                                {editIcon}
                                            </span>
                                            <span className="icon-btn" onClick={(e) => {
                                                e.stopPropagation();
                                                openConfirmDialog(zone.id, zone.zone_name);
                                            }}>
                                                {deleteIcon}
                                            </span>
                                        </Col>
                                    </Row>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="zone-additional-info">
                                        <div>
                                            <p>{t('address')}</p>
                                            <p>
                                                <ShowMoreText
                                                    lines={2}
                                                    more={t('showMore')}
                                                    less={t('showLess')}
                                                    anchorClass="show-more-link"
                                                    expanded={false}
                                                    width={0}
                                                >
                                                    {zone.address}
                                                </ShowMoreText>
                                            </p>
                                        </div>
                                        <div>
                                            <p>{t('createdBy')}</p>
                                            <p>{zone.user_name} {t('createdOn')} {moment(zone.created_ts).format('MM/DD/YYYY')}</p>
                                        </div>
                                        <div>
                                            <p>{t('coordinates')}</p>
                                            <p>{truncateDecimals(zone.lat, 3)}, {truncateDecimals(zone.lon, 3)}</p>
                                        </div>
                                        <div>
                                            <p>{t('radius')}</p>
                                            <p>{zone.radius}</p>
                                        </div>
                                    </div>
                                    <div className="accordion-body-actions">
                                        <CustomButton
                                            variant='primary-outline'
                                            prefix="flex-btn add-zone-btn"
                                            onClick={() => zoomToZone({ lat: zone.lat, lng: zone.lon }, zone.radius)}
                                        >
                                            {iconSearch}
                                            {t('zoomOnMap')}
                                        </CustomButton>
                                        <CustomButton
                                            variant='primary-outline'
                                            prefix="flex-btn add-zone-btn"
                                            onClick={() => generateReport(zone.id)}
                                        >
                                            {iconReport}
                                            {t('generateReport')}
                                        </CustomButton>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            </div>
            {confirmModal ? (
                <Modal
                    size='lg'
                    show={confirmModal}
                    onHide={closeConfirmDialog}
                    className="modal-lg-size"
                    dialogClassName="has-error"
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="contained-modal-title-lg" className='delete-title'>{t('deleteZoneTitle')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='modal-text'>{t('deleteZoneBody', { zone_name: deletedZone.name })}</div>
                    </Modal.Body>
                    <Modal.Footer bsPrefix="default-modal-footer modal-footer">
                        <CustomButton variant="primary-outline" onClick={closeConfirmDialog}>{t('btnCancel')}</CustomButton>
                        <CustomButton variant="delete" onClick={confirmZoneDelete}>{t('btnDelete')}</CustomButton>
                    </Modal.Footer>
                </Modal>
            ) : ''}
        </>
    )
}
